import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { GlowFrame } from './GlowFrame';
import type { FormFieldConfig } from '../types/contact';
interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  fields: FormFieldConfig[];
  initialValues?: Record<string, string>;
  submitLabel: string;
  buildMailto: (values: Record<string, string>) => string;
}
export function FormModal({
  isOpen,
  onClose,
  title,
  description,
  fields,
  initialValues = {},
  submitLabel,
  buildMailto
}: FormModalProps) {
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [submitted, setSubmitted] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    setValues(initialValues);
    setSubmitted(false);
    previouslyFocused.current = document.activeElement as HTMLElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      previouslyFocused.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const handleChange = (id: string, value: string) => {
    setValues(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    window.location.href = buildMailto(values);
    setSubmitted(true);
  };
  return <AnimatePresence data-id="element-84" data-mp="37ongvffhnt3h">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-id="element-85" data-mp="37potvpg2epnu">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 bg-ink/90 backdrop-blur-md" onClick={onClose} aria-hidden="true" data-id="element-86" data-mp="25c0ley0e1hxs" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.97,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} role="dialog" aria-modal="true" aria-label={title} className="relative z-10 w-full max-w-md border border-led/20 bg-ink p-8 shadow-[0_0_0_1px_rgba(242,247,255,0.08),0_0_50px_-10px_rgba(143,211,240,0.35)]" data-id="element-87" data-mp="2j35oka2ysscv">
            <button ref={closeButtonRef} type="button" onClick={onClose} className="absolute right-5 top-5 text-led/60 transition-colors duration-300 hover:text-ice focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice" aria-label="Close form" data-id="element-88" data-mp="13nf31if9vg10">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-89" data-mp="qdd7wqnir547" />
            </button>

            {submitted ? <div className="py-6 text-center" data-id="element-90" data-mp="md2skndu72co">
                <h3 className="font-heading text-2xl uppercase tracking-tight text-led" data-id="element-91" data-mp="magj2i7dycrt">Thank you</h3>
                <p className="mt-3 text-sm text-led/60" data-id="element-92" data-mp="1j06cjk414ojh">
                  Your email client should have opened with the details prefilled. We will be in touch
                  shortly.
                </p>
              </div> : <>
                <h3 className="font-heading text-2xl uppercase tracking-tight text-led" data-id="element-93" data-mp="151zlamtl5mss">{title}</h3>
                {description && <p className="mt-2 text-sm text-led/60" data-id="element-94" data-mp="3utedim4zhycq">{description}</p>}
                <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-id="element-95" data-mp="3prytmxejlxvx">
                  {fields.map(field => <div key={field.id} data-id="element-96" data-mp="1hizac8oxctm9">
                      <label htmlFor={field.id} className="mb-2 block text-[10px] uppercase tracking-[0.2em] text-led/50" data-id="element-97" data-mp="1maqrxtq3rp09">
                        {field.label}
                      </label>
                      {field.type === 'textarea' ? <textarea id={field.id} required={field.required} placeholder={field.placeholder} value={values[field.id] || ''} onChange={event => handleChange(field.id, event.target.value)} rows={3} className="w-full resize-none border border-led/20 bg-transparent px-3 py-2 text-sm text-led placeholder:text-led/30 focus:border-ice focus:outline-none" data-id="element-98" data-mp="20ajpo901r5hq" /> : <input id={field.id} type={field.type} required={field.required} placeholder={field.placeholder} value={values[field.id] || ''} onChange={event => handleChange(field.id, event.target.value)} className="w-full border border-led/20 bg-transparent px-3 py-2 text-sm text-led placeholder:text-led/30 focus:border-ice focus:outline-none" data-id="element-99" data-mp="cc98f96z7arx" />}
                    </div>)}
                  <GlowFrame as="button" type="submit" interactive className="mt-2 w-full px-6 py-3 text-center text-xs uppercase tracking-[0.2em] text-led" data-id="element-100" data-mp="3dxaem3s5apg">
                    {submitLabel}
                  </GlowFrame>
                </form>
              </>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}